import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ImpersonationSession } from "../api/types";

/**
 * §9.1's explicit UI requirement for tenant impersonation: "must be time-boxed, logged, and
 * visibly bannered in the UI ('You are viewing as [tenant] — this session is logged') so it
 * can never be used silently." The logging half is already real and server-side
 * (`POST /platform/tenants/{id}/impersonate` writes a `platform_audit_log` entry BEFORE
 * returning the token — see `backend/BACKEND_ARCHITECTURE.md` §10.7). This context is the
 * frontend's half: it holds the ONE active impersonation session (this app never allows more
 * than one at a time — starting a new one replaces any prior one) so `ImpersonationBanner`
 * can render on every route, not just the tenant-detail page, for as long as the session is
 * genuinely active — the whole point of "impossible to miss."
 *
 * Deliberately a separate context from `PlatformAuthContext` — an impersonation session is a
 * tenant-realm credential (`aud: "tenant"`), not a platform-operator one; conflating the two
 * would blur exactly the realm boundary §9.1 is built to keep separate.
 */
export interface ActiveImpersonation {
  session: ImpersonationSession;
  tenantName: string;
  startedAt: string;
}

export interface ImpersonationContextValue {
  active: ActiveImpersonation | null;
  /** Milliseconds until `active.session.expires_at`, clamped to >= 0. Recomputed every
   * second while a session is active so the banner's countdown is genuinely live. */
  remainingMs: number;
  start: (session: ImpersonationSession, tenantName: string) => void;
  end: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveImpersonation | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active]);

  const remainingMs = active ? Math.max(0, new Date(active.session.expires_at).getTime() - now) : 0;

  // Auto-clear the instant the 15-minute window elapses — a stale "you are viewing as X"
  // banner past real expiry would be worse than none at all (implies a session that no
  // longer works is still active).
  useEffect(() => {
    if (active && remainingMs === 0) setActive(null);
  }, [active, remainingMs]);

  const start = useCallback((session: ImpersonationSession, tenantName: string) => {
    setActive({ session, tenantName, startedAt: new Date().toISOString() });
  }, []);

  const end = useCallback(() => setActive(null), []);

  const value = useMemo<ImpersonationContextValue>(
    () => ({ active, remainingMs, start, end }),
    [active, remainingMs, start, end],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation() must be used within an <ImpersonationProvider>");
  return ctx;
}
